import { Badge, Box, Button, CircularProgress, Dialog, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import { useMeetingActionsContext } from '../../contexts/meetingactions.context'
import { useToggleButtonsData } from '../../hooks/useToggleButtonsData'
import { useSocketsContext } from '../../contexts/sockets.context'
import DataLoadingSpinner from '../common/DataLoadingSpinner'
import { useMeetingStore } from '../../store/meeting.store'
import OnlineMeetingTimer from './OnlineMeetingTimer'
import { Icons } from '../../utils/utilities'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'
import AgoraStats from './AgoraStats'

const Header = observer( ( { meetingContainer } ) => {

    const [showTimeExtenderWindow, setShowTimeExtenderWindow] = useState( false )
    const [extendingMeetingTime, setExtendingMeetingTime] = useState( false )
    const [hideStartTimer, setHideStartTimer] = useState( false )
    const [hideEndTimer, setHideEndTimer] = useState( true )

    const { connectionLost, agoraServerDisconnected, leaveChannel, toggleScreenRecording } = useMeetingActionsContext()

    // const { emitEvent, disconnected: socketServerDisconnected } = useSocketsContext()

    const { recordButtonDetails } = useToggleButtonsData()

    const { palette, layout, border, breakpoints } = useTheme()

    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()
    const MeetingStore = useMeetingStore()
    const { meeting_id } = useParams()

    const smallWidth = useMediaQuery( breakpoints.down( 'sm' ) )

    const onTimerShown = useCallback( () => {
        setShowTimeExtenderWindow( true )
        setHideEndTimer( false )
    }, [] )

    const handleStartTimeReach = useCallback( () => {
        setHideStartTimer( true )
    }, [] )

    const hideTimer = useCallback( () => setHideEndTimer( true ), [] )

    const openParticipantsList = () => {
        const openFlag = MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'participants' ? true : !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen
        MeetingStore.setMeetingRoomLayoutSettings( {
            leftSideCollapseTab: 'participants',
            isLeftSideCollapseOpen: openFlag
        } )
    }

    const extendMeetingTime = async minutes => {
        try {
            const payload = {
                update_type: "extend-time",
                extend_time: minutes * 60
            }
            setExtendingMeetingTime( minutes )
            const { success, data, message } = await MeetingStore.updateMeetingDetails( meeting_id, payload )

            if ( !success )
                toast( message )
            else {
                await OnlineSessionSocketStore.emit( 'meeting:time-extended', { room_id: meeting_id, data } )
                setShowTimeExtenderWindow( false )
            }
            setExtendingMeetingTime( false )
        } catch ( err ) {
            setShowTimeExtenderWindow( false )
        }

    }

    return (
        <Box padding="0 10px" borderRadius="10px" display="grid" gridTemplateColumns="1fr 1fr 1fr" alignItems="center" justifyContent="space-between">

            {MeetingStore.getCurrentMeeting.created_by === MeetingStore.getLocalUser.user_id && <Dialog
                sx={{
                    zIndex: 6000
                }}
                onClose={() => setShowTimeExtenderWindow( false )}
                open={showTimeExtenderWindow}
                slotProps={{
                    paper: {
                        sx: {
                            width: "95%",
                            maxWidth: "500px"
                        }
                    }
                }}
                container={meetingContainer.current}
            >
                <Box padding="20px">
                    <Typography gutterBottom fontSize="18px" variant='h6'>{MeetingStore.getCurrentMeeting.meeting_type === 'webinar' ? "Webinar" : MeetingStore.getCurrentMeeting.meeting_type === 'class' ? "Online Class" : "Online Meeting"} is ending!</Typography>
                    <Typography fontSize="14px">
                        The {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' ? "meeting" : MeetingStore.getCurrentMeeting.meeting_type} is ending, please extend your time for extra time. All the users will be removed from the meeting once the time ends.
                    </Typography>
                    <Box marginTop="20px" display="flex" gap="10px">
                        <Button sx={{ borderRadius: "20px" }} variant='contained' disableElevation disabled={Boolean( extendingMeetingTime )} startIcon={extendingMeetingTime === 5 && <CircularProgress color='greyed' size={20} />} onClick={() => extendMeetingTime( 5 )}>+ 5 min</Button>
                        <Button sx={{ borderRadius: "20px" }} variant='contained' disableElevation disabled={Boolean( extendingMeetingTime )} startIcon={extendingMeetingTime === 10 && <CircularProgress color='greyed' size={20} />} onClick={() => extendMeetingTime( 10 )}>+ 10 min</Button>
                        <Button sx={{ borderRadius: "20px" }} variant='outlined' onClick={() => setShowTimeExtenderWindow( false )} color="error">Dismiss</Button>
                    </Box>
                </Box>
            </Dialog>}

            <Box>
                <Box>
                    <Typography component="h1" lineHeight={1.2} width="fit-content" maxWidth="40vw" noWrap fontSize="14px" fontWeight="600">{MeetingStore.getCurrentMeeting.title}</Typography>
                    <Typography fontSize="10px" textTransform="uppercase" component="em" color='textSecondary'>{MeetingStore.getCurrentMeeting.meeting_type}</Typography>
                </Box>
            </Box>

            <Box>
                {connectionLost && !( OnlineSessionSocketStore.isDisconnected || agoraServerDisconnected ) &&
                    < Box position="absolute" top="10px" left="50%" bgcolor={palette.contentBg} zIndex={10} padding="20px" borderRadius="20px" width="max-content" maxWidth="calc(100% - 40px)" sx={{ transform: "translateX(-50%)" }} display="flex" alignItems="center" justifyContent="center">
                        <DataLoadingSpinner size={30} padding='0' fontSize="30px" waitingMessage="Your internet got disconnected, please wait for reconnection..." />
                    </Box>
                }
                {MeetingStore.getCurrentMeeting.created_by === MeetingStore.getLocalUser.user_id && !hideStartTimer && <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography fontSize="10px" color='textSecondary' letterSpacing="0.1em"  >STARTS IN</Typography>
                    <OnlineMeetingTimer onEnd={handleStartTimeReach} styles={{ textAlign: "center" }} fontSize="16px" />
                </Box>}
                {hideEndTimer &&
                    <OnlineMeetingTimer fontSize="14px" showIfLessThan={600} onShow={onTimerShown} deadline={MeetingStore.getCurrentMeeting.ends_at} styles={{
                        textAlign: "center",
                    }} />}
                {!hideEndTimer && <Box display="flex" flexDirection="column" alignItems="center">
                    <Box top="0" bgcolor={palette.error.light + 22} border={`2px solid ${palette.error.light + "44"}`} borderRadius="20px" display="flex" width="fit-content" gap={{ xs: "10px", sm: "20px" }} alignItems="center" justifyContent="space-between" padding="5px 10px">
                        <Box >
                            <Typography fontSize="8px" color='error'>SESSION ENDING</Typography>
                            <Typography variant='subtitle2' display="flex" alignItems="center" gap="10px" fontSize="18px" > <OnlineMeetingTimer onHide={hideTimer} fontSize="14px" showIfLessThan={600} onEnd={leaveChannel} deadline={MeetingStore.getCurrentMeeting.ends_at} styles={{
                                textAlign: "center",
                                // color: "error.main"
                            }} />
                            </Typography>
                        </Box>
                        {MeetingStore.getCurrentMeeting.created_by === MeetingStore.getLocalUser.user_id && <Tooltip disableInteractive title="Add more time">
                            <IconButton sx={{ display: { xs: "flex", sm: "none" }, color: "white", borderColor: "white", borderRadius: "20px", bgcolor: palette.primary.main, ":hover": { bgcolor: palette.primary.dark } }} color='primary' onClick={() => setShowTimeExtenderWindow( true )} size='small' >{Icons.default.AddIcon}</IconButton>
                        </Tooltip>}
                        {MeetingStore.getCurrentMeeting.created_by === MeetingStore.getLocalUser.user_id && <Button sx={{ display: { xs: "none", sm: "flex" }, borderRadius: "20px" }} color='primary' variant='contained' startIcon={Icons.default.AddIcon} onClick={() => setShowTimeExtenderWindow( true )} size='small' >Add Time</Button>}
                    </Box>
                </Box>}
            </Box>
            <Box height="100%" display="flex" gap={layout.cardsGap} justifyContent="flex-end">

                <AgoraStats />

                {MeetingStore.getLocalUser.user_id === MeetingStore.getCurrentMeeting.created_by && recordButtonDetails.isOn && <Tooltip disableInteractive title={recordButtonDetails.tooltip}>
                    <Box border={`1px solid ${palette.text.secondary}`} display="flex" gap="10px" alignItems="center" borderRadius="20px" bgcolor={palette.rowHover}>
                        <Box fontSize="24px" display="flex" height="100%" alignItems="center" padding="5px 0 5px 10px">
                            {recordButtonDetails.startIcon}
                        </Box>
                        <Box>
                            <Typography display={{ md: "block", xs: "none" }} fontSize="12px">Recording...</Typography>
                            <OnlineMeetingTimer styles={{ fontSize: "10px", marginTop: "-2px", display: "block" }} type='timer' startsAt={MeetingStore.getMeetingState.started_recording_at} />
                        </Box>
                        <IconButton size={smallWidth ? "small" : "medium"} disabled={MeetingStore.getMeetingState.startingOrStopingRecording} sx={{ paddingX: { xs: "10px", sm: "15px" }, borderRadius: "20px" }} onClick={toggleScreenRecording}>
                            {MeetingStore.getMeetingState.startingOrStopingRecording ? <CircularProgress size={20} /> : recordButtonDetails.endIcon}
                        </IconButton>
                    </Box>
                </Tooltip>}

                <Tooltip disableInteractive title="Participants">
                    <Box border={`1px solid ${palette.text.secondary}`} component="button" onClick={openParticipantsList} bgcolor={palette.rowHover} sx={{ cursor: "pointer", ":hover": { bgcolor: palette.action.hover } }} padding="2px 10px 2px 2px" height="100%" borderRadius="20px" display="flex" alignItems="center" gap="10px">
                        <Typography height="100%" borderRadius="20px" justifyContent="center" bgcolor={palette.primary.light + 99} sx={{ aspectRatio: "1 / 1" }} margin="0" alignItems="center" display="flex" fontSize="18px">
                            {Icons.default.People}
                        </Typography>
                        <Typography margin="0" fontSize="14px" fontWeight="800">
                            {MeetingStore.getRemoteUsers.length + 1}
                        </Typography>
                    </Box>
                </Tooltip>

                {/* <Button color='greyed' startIcon={Icons.default.Group} variant='text' sx={{ display: { xs: "none", md: "flex" } }} onClick={openParticipantsList} >
                    <Badge badgeContent={MeetingStore.getRemoteUsers.length + 1} color='primary'  >
                        Participants
                    </Badge>
                </Button> */}
            </Box>
        </Box>
    )
} )

export default Header
